import React, { ReactNode } from 'react';

// icons
import CheckCircle from '@mui/icons-material/CheckCircle';
// material ui
import CircularProgress from '@mui/material/CircularProgress';

import styled from 'styled-components';

// constants
import aampeTheme from '../../constants/aampeTheme';

interface BasicButtonStyledProps {
    isPrimary?: boolean;
    isDisabled?: boolean;
    isSuccess?: boolean;
    isCompact?: boolean;
    isWarning?: boolean;
    isBorder?: boolean;
}

export interface BasicButtonProps
    extends React.DetailedHTMLProps<
            React.ButtonHTMLAttributes<HTMLButtonElement>,
            HTMLButtonElement
        >,
        BasicButtonStyledProps {
    name?: string;
    action?: (e?: any) => void;
    icon?: any;
    isLoading?: boolean;
    isTrailingIcon?: boolean;
    numberBadge?: ReactNode;
    isOutlined?: boolean;
}

const BasicButton: React.FC<React.PropsWithChildren<BasicButtonProps>> = ({
    name,
    action,
    icon,
    isPrimary = true,
    isDisabled = false,
    isLoading = false,
    isSuccess = false,
    isCompact = false,
    isWarning = false,
    isTrailingIcon = false,
    isBorder = true,
    style,
    numberBadge,
    ...props
}) => {
    return (
        <Button
            disabled={isDisabled || isSuccess || isLoading}
            onClick={action}
            isPrimary={isPrimary}
            isDisabled={isDisabled}
            isSuccess={isSuccess}
            isCompact={isCompact}
            isWarning={isWarning}
            isBorder={isBorder}
            style={{ ...style }}
            {...props}
        >
            {isLoading ? (
                <CircularProgress
                    size={isCompact ? 16 : 24}
                    color={'inherit'}
                />
            ) : isSuccess ? (
                <CheckCircle />
            ) : (
                <ButtonContent>
                    {!isTrailingIcon && icon}
                    {name}
                    {numberBadge && numberBadge}
                    {isTrailingIcon && icon}
                </ButtonContent>
            )}
        </Button>
    );
};

export const Button = styled.button.withConfig({
    shouldForwardProp: (prop) =>
        ![
            'isCompact',
            'isDisabled',
            'isPrimary',
            'isWarning',
            'isSuccess',
            'isBorder',
            'isOutlined', // Add this to prevent DOM warnings
        ].includes(prop),
})<BasicButtonStyledProps>(
    ({ isCompact, isDisabled, isPrimary, isWarning, isSuccess, isBorder }) => ({
        width: 'fit-content',
        padding: '0 16px',
        height: '36px',
        boxSizing: 'border-box',
        minWidth: isCompact ? '80px' : '120px',
        borderRadius: aampeTheme.borderRadius.default,
        fontSize: aampeTheme.size.font.default,
        fontFamily: 'inherit',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        pointerEvents: isDisabled ? 'none' : 'inherit',
        boxShadow: isPrimary ? 'none' : '0px 1px 2px rgba(16, 24, 40, 0.05)',
        backgroundColor: isWarning
            ? aampeTheme.colors.warningRed
            : isSuccess
            ? aampeTheme.colors.success500
            : isPrimary
            ? aampeTheme.colors.solidA01
            : aampeTheme.colors.white,
        color: isSuccess
            ? aampeTheme.colors.white
            : isPrimary
            ? aampeTheme.colors.white
            : aampeTheme.colors.solidA01,
        border: !isBorder
            ? 'none'
            : `1px solid ${
                  isWarning
                      ? aampeTheme.colors.error500
                      : aampeTheme.colors.solidA01
              }`,

        '&:disabled': {
            backgroundColor: isWarning
                ? aampeTheme.colors.error50
                : isPrimary
                ? aampeTheme.colors.interactiveA01
                : aampeTheme.colors.white,
            color: isPrimary
                ? aampeTheme.colors.textAccessibleA02
                : aampeTheme.colors.interactiveA01,
            border: !isBorder
                ? 'none'
                : `1px solid ${
                      isWarning
                          ? aampeTheme.colors.error50
                          : isPrimary
                          ? aampeTheme.colors.primary50
                          : aampeTheme.colors.solidA01
                  }`,
            cursor: 'not-allowed',
            pointerEvents: 'none',
        },

        '&:hover': {
            backgroundColor: isWarning
                ? aampeTheme.colors.error700
                : isPrimary
                ? aampeTheme.colors.solidA02
                : aampeTheme.colors.white,
            color: isPrimary
                ? aampeTheme.colors.white
                : aampeTheme.colors.solidA02,
            border: !isBorder
                ? 'none'
                : `1px solid ${
                      isWarning
                          ? aampeTheme.colors.error700
                          : aampeTheme.colors.solidA02
                  }`,
        },

        '&:active': {
            backgroundColor: isWarning
                ? aampeTheme.colors.error500
                : isPrimary
                ? aampeTheme.colors.solidA01
                : aampeTheme.colors.white,
            color: isPrimary
                ? aampeTheme.colors.white
                : aampeTheme.colors.solidA01,
            border: !isBorder
                ? 'none'
                : `1px solid ${
                      isWarning
                          ? aampeTheme.colors.error50
                          : aampeTheme.colors.solidA01
                  }`,
        },
    }),
);

const ButtonContent = styled.div(() => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: aampeTheme.spacing(0.5),
    paddingLeft: aampeTheme.spacing(1),
    paddingRight: aampeTheme.spacing(1),
}));

export default BasicButton;
